'use client';

import React from 'react';
import Link from 'next/link';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from '@/app/shared/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { NavigationItemWithRoute } from '@/types/types';
import { ChevronDownIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface MainNavigationProps {
  navItems: NavigationItemWithRoute[];
}

const MainNavigation: React.FC<MainNavigationProps> = ({ navItems }) => {
  return (
    <div className='flex items-center gap-6 text-sm ml-2 mr-2 h-navbar'>
      <NavigationMenu>
        <NavigationMenuList className='gap-8'>
          {navItems.map((navItem) => (
            <NavigationMenuItem
              key={navItem.text}
              className='text-designer-link-gray flex items-center'
            >
              {navItem.submenu && navItem.submenu.length > 0 ? (
                <>
                  <NavigationMenuTrigger className='flex items-center gap-2 hover:text-white transition-colors duration-300 p-0 text-sm'>
                    <span>{navItem.text}</span>
                    <ChevronDownIcon className='size-4' />
                  </NavigationMenuTrigger>

                  <NavigationMenuContent asChild>
                    <AnimatePresence mode='wait'>
                      <motion.ul
                        key={navItem.text}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className='w-80 p-3'
                        role='menu'
                      >
                        {navItem.submenu.map((subItem) => (
                          <li key={subItem.url} role='none'>
                            <NavigationMenuLink asChild>
                              <Link
                                href={subItem.url}
                                role='menuitem'
                                className={cn(
                                  'flex select-none gap-4 rounded-md p-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
                                )}
                              >
                                <span>{subItem.text}</span>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </motion.ul>
                    </AnimatePresence>
                  </NavigationMenuContent>
                </>
              ) : (
                <NavigationMenuLink asChild>
                  <Link
                    href={navItem.url}
                    className='flex items-center gap-2 hover:text-white p-0 transform transition-transform duration-300 hover:scale-105 ease-in-out'
                  >
                    <span>{navItem.text}</span>
                  </Link>
                </NavigationMenuLink>
              )}
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  );
};

export default MainNavigation;
